//libs\core\shell\src\lib\system\system-config.service.ts
import { Injectable, computed, inject, signal } from '@angular/core';
import {
  DEFAULT_SHELL_SYSTEM_CONFIG,
  type ShellSystemConfig,
} from './system-config.interface';
import { APP_CONFIG } from '@cardapio-online/config';

interface SystemConfigStoragePayload {
  version: number;
  data: Partial<ShellSystemConfig>;
}

@Injectable({
  providedIn: 'root',
})
export class SystemConfigService {
    private static readonly STORAGE_VERSION = 1;

  private readonly cfg = inject(APP_CONFIG);

  private readonly configState = signal<ShellSystemConfig>(
    this.readStoredConfig()
  );

  readonly config = computed(() => this.configState());
  readonly slug = computed(() => this.resolveSlug());
  readonly storageKey = computed(() => this.buildStorageKey());

  snapshot(): ShellSystemConfig {
    return this.configState();
  }

  update(next: Partial<ShellSystemConfig>): ShellSystemConfig {
    const merged: ShellSystemConfig = {
      ...this.configState(),
      ...next,
      updatedAt: new Date().toISOString(),
    };

    this.configState.set(merged);
    this.persist(merged);

    return merged;
  }

  reset(): ShellSystemConfig {
    const next: ShellSystemConfig = {
      ...DEFAULT_SHELL_SYSTEM_CONFIG,
      updatedAt: new Date().toISOString(),
    };

    this.configState.set(next);
    this.persist(next);

    return next;
  }

  private readStoredConfig(): ShellSystemConfig {
    if (typeof window === 'undefined') {
      return DEFAULT_SHELL_SYSTEM_CONFIG;
    }

    const rawValue = localStorage.getItem(this.buildStorageKey());

    if (!rawValue) {
      return DEFAULT_SHELL_SYSTEM_CONFIG;
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<SystemConfigStoragePayload>;

      if (
        parsed.version !== SystemConfigService.STORAGE_VERSION ||
        !parsed.data
      ) {
        return DEFAULT_SHELL_SYSTEM_CONFIG;
      }

      return {
        ...DEFAULT_SHELL_SYSTEM_CONFIG,
        ...parsed.data,
      };
    } catch {
      return DEFAULT_SHELL_SYSTEM_CONFIG;
    }
  }

  private persist(config: ShellSystemConfig): void {
    if (typeof window === 'undefined') {
      return;
    }

    const payload: SystemConfigStoragePayload = {
      version: SystemConfigService.STORAGE_VERSION,
      data: config,
    };

    localStorage.setItem(this.buildStorageKey(), JSON.stringify(payload));
  }

  private buildStorageKey(): string {
    return `menuhub:${this.resolveSlug()}:admin:system:config`;
  }

  private resolveSlug(): string {
    if (typeof window === 'undefined') {
      return 'server';
    }

    const configuredTenant =
      'tenantSlug' in this.cfg
        ? String((this.cfg as { tenantSlug?: string }).tenantSlug ?? '')
            .trim()
            .toLowerCase()
        : '';

    if (configuredTenant) {
      return configuredTenant;
    }

    return window.location.hostname.trim().toLowerCase() || 'default';
  }


}
